import {
  Campaign,
  CheckCircle,
  DynamicFeed,
  Contacts,
  Info,
  Schedule,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Container,
} from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import TemplatePreview from "../../widgets/TemplatePreview";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { GlassButtonLoading } from "../../themes/UiElements";
import { useDialog } from "../../context/DialogContext";
import SplitsDisplay from "./contacts/SplitsDisplay";
import { UserContext } from "../../context/UserProvider";
const StatItem = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {icon}
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="medium">
      {value}
    </Typography>
  </Box>
);

const RdCampaignStep3 = ({ campaignPost, setCampaignPost, onConfirmSplit }) => {
  const { showDialog } = useDialog();
  const [testNumber, setTestNumber] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitsData, setSplitsData] = useState([]);
  const { user: userData, loading } = useContext(UserContext);

  const handleTestSend = useCallback(async () => {
    if (testNumber.length < 4) {
      toast.error("Please enter valid number");
      return;
    }
    const payload = {
      templateId: campaignPost.template.id,
      contact: testNumber,
      header: campaignPost.header,
    };
    if (campaignPost.contacts.length < 1) {
      toast.error("Contact list is empty.");
      return;
    }
    const isStatic =
      campaignPost?.isStaticParams && campaignPost.bodyParams.length > 0;
    if (isStatic) {
      const checkP = campaignPost.bodyParams.filter((item) => item === "");
      if (checkP.length > 0) {
        toast.error("Please ensure you have entered all static parameters");
        return;
      }
    }
    payload.bodyParams = isStatic
      ? campaignPost.bodyParams
      : Object.values(campaignPost.contacts[0]).slice(1);
    try {
      setTestSending(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/test-campaign`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setTestSending(false);
    }
  }, [testNumber]);
  const handleSplitSubmit = useCallback(async () => {
    try {
      setSplitLoading(true);
      const payload = {
        count: campaignPost.contacts.length,
      };
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/split-campaign`,
        payload,
        getHeader()
      );
      const splitsL = response.data.splits;
      setCampaignPost((prev) => ({
        ...prev,
        dataSplitsList: splitsL,
      }));
      setSplitsData(splitsL);
    } catch (error) {
      showDialog(
        error?.response?.data?.title || "Error",
        `${getErrorResponse(error)}`,
        {
          type: "error",
          onConfirm: () => {},
        }
      );
    } finally {
      setSplitLoading(false);
    }
  }, [campaignPost]);

  return (
    <Container maxWidth="md">
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
            >
              <Campaign color="primary" />
              Campaign Summary
            </Typography>
            <Divider />
          </Box>

          <Stack spacing={2}>
            <StatItem
              icon={<Info color="primary" fontSize="small" />}
              label="Campaign Name"
              value={campaignPost.campaignName}
            />
            <StatItem
              icon={<CheckCircle color="primary" fontSize="small" />}
              label="Selected Template"
              value={`${campaignPost.template.name} (${campaignPost.template.status})`}
            />
            <StatItem
              icon={<DynamicFeed color="primary" fontSize="small" />}
              label="Parameter Type"
              value={
                <Chip
                  label={campaignPost.isStaticParams ? "Static" : "Dynamic"}
                  size="small"
                  color={campaignPost.isStaticParams ? "default" : "primary"}
                />
              }
            />
            <StatItem
              icon={<Schedule color="primary" fontSize="small" />}
              label="Campaign Schedule"
              value={
                campaignPost.isCampaignScheduled
                  ? campaignPost?.scheduledDate
                    ? format(
                        new Date(campaignPost.scheduledDate),
                        "dd/MM/yyyy hh:mm a"
                      )
                    : "NA"
                  : "Immediate"
              }
            />
            <StatItem
              icon={<Contacts color="primary" fontSize="small" />}
              label="Total Contacts"
              value={campaignPost.contactsCount || campaignPost.contacts.length}
            />
          </Stack>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Preview
            </Typography>
            <TemplatePreview
              template={campaignPost.template}
              previewUrl={campaignPost.header?.mediaUrl ?? ""}
            />
          </Box>

          <Box>
            <Box mt={2} textAlign="right">
              <GlassButtonLoading
                variant="contained"
                color="primary"
                loading={splitLoading}
                onClick={handleSplitSubmit}
              >
                Use Ai to Determine Campaign Flow
              </GlassButtonLoading>

              {splitsData && splitsData.length > 0 && (
                <Box my={1}>
                  <SplitsDisplay splitsData={splitsData} />
                </Box>
              )}
            </Box>
            <Box mt={1}>
              {/* {(userData.accounts ?? []).length > 0 && (
                <SpreadAcrossAccounts
                  userData={userData}
                  onSelectionChange={(selected) => {
                    setCampaignPost((prev) => ({
                      ...prev,
                      splitAccounts: selected,
                    }));
                  }}
                />
              )} */}
              <Box my={1} textAlign={"right"}>
                <GlassButtonLoading
                  onClick={() => {
                    showDialog(
                      "Please confirm!",
                      "Are you sure you want to confirm this campaign?",
                      {
                        type: "warning",
                        onConfirm: () => {
                          onConfirmSplit?.();
                        },
                      }
                    );
                  }}
                >
                  Confirm this flow for campaign
                </GlassButtonLoading>
              </Box>
            </Box>
            {/* <>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Phone color="primary" /> Test Campaign
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Send a test message to verify your campaign. Parameters will be
                picked from the first contact in your list.
              </Alert>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  size="small"
                  label="Phone Number"
                  variant="outlined"
                  value={testNumber}
                  type="number"
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="Enter phone number with country code"
                  fullWidth
                />
                <LoadingButton
                  variant="contained"
                  sx={{ width: 200 }}
                  loading={testSending}
                  startIcon={<Send />}
                  onClick={handleTestSend}
                  disabled={!testNumber}
                >
                  Send Test
                </LoadingButton>
              </Stack>
            </> */}
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default RdCampaignStep3;
